import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import PolicyIcon from '@mui/icons-material/Policy';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import TaskIcon from '@mui/icons-material/Task';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FunctionsIcon from '@mui/icons-material/Functions';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

/**
 * Available mention types that appear when user types "@"
 */
const MENTION_TYPES = [
  { id: 'policy', label: 'policy', description: 'Mention a policy' },
  { id: 'tasks', label: 'tasks', description: 'Mention a task' },
  { id: 'documents', label: 'documents', description: 'Mention a document from storage' },
  { id: 'functions', label: 'functions', description: 'Mention a function' },
  { id: 'subscribers', label: 'webhooks', description: 'Mention a webhook' },
];

/**
 * Get icon for file type based on name/extension
 */
const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];
  const docExts = ['doc', 'docx', 'txt', 'rtf'];
  const spreadsheetExts = ['xls', 'xlsx', 'csv'];
  const jsonExts = ['json'];

  if (imageExts.includes(ext)) return <ImageIcon sx={{ fontSize: 18, color: 'primary.500' }} />;
  if (pdfExts.includes(ext)) return <PictureAsPdfIcon sx={{ fontSize: 18, color: '#e53935' }} />;
  if (docExts.includes(ext)) return <DescriptionIcon sx={{ fontSize: 18, color: '#1976d2' }} />;
  if (spreadsheetExts.includes(ext)) return <TableChartIcon sx={{ fontSize: 18, color: '#388e3c' }} />;
  if (jsonExts.includes(ext)) return <DataObjectIcon sx={{ fontSize: 18, color: '#f57c00' }} />;

  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'neutral.500' }} />;
};

const TASK_STATUS_COLORS = {
  created: 'neutral',
  'in progress': 'primary',
  'action required': 'warning',
  completed: 'success',
  failed: 'danger',
};

const getDocumentItemKey = (item, index) => {
  const pathKey = typeof item?.path === 'string' ? item.path.trim() : '';
  if (pathKey) return pathKey;

  const idKey = typeof item?.id === 'string' || typeof item?.id === 'number'
    ? String(item.id).trim()
    : '';
  if (idKey) return idKey;

  const typeKey = item?.type || 'item';
  const nameKey = (item?.name || 'unnamed').trim();
  return `${typeKey}:${nameKey}:${index}`;
};

/**
 * Popover component for mentioning policies, tasks, documents, functions, and subscribers in chat.
 * Supports six modes:
 * 1. "types" mode - shows available mention types (e.g., @policy, @tasks, @documents, @functions, @subscribers)
 * 2. "policies" mode - shows actual policies from API
 * 3. "tasks" mode - shows tasks from API with pagination
 * 4. "documents" mode - shows S3 files/folders with folder navigation
 * 5. "functions" mode - shows available functions from API
 * 6. "subscribers" mode - shows available subscribers from API
 */
const PolicyMentionPopover = ({
  open,
  anchorEl,
  inline = false,
  query = '',
  mode = 'types', // 'types', 'policies', 'tasks', 'documents', 'functions', or 'subscribers'
  policies = [],
  tasks = [],
  loading = false,
  tasksLoading = false,
  hasMoreTasks = false,
  // Document props
  documents = [],
  documentFolders = [],
  documentsLoading = false,
  documentBreadcrumbs = [],
  onNavigateFolder,
  onNavigateDocumentRoot,
  onSelectDocument,
  // Functions props
  functions = [],
  functionsLoading = false,
  onSelectFunction,
  // Subscribers props
  subscribers = [],
  subscribersLoading = false,
  onSelectSubscriber,
  // Common props
  onSelectType,
  onSelectPolicy,
  onSelectTask,
  onLoadMoreTasks,
  onClose,
  highlightedIndex,
}) => {
  const listRef = useRef(null);
  const sheetRef = useRef(null);

  // Filter mention types based on query
  const filteredTypes = useMemo(() => {
    if (!query) return MENTION_TYPES;
    const lowerQuery = query.toLowerCase();
    return MENTION_TYPES.filter(
      (type) =>
        type.id.toLowerCase().includes(lowerQuery) ||
        type.label.toLowerCase().includes(lowerQuery)
    );
  }, [query]);

  // Filter policies based on query (only the part after "policy")
  const filteredPolicies = useMemo(() => {
    if (mode !== 'policies') return [];
    // Extract query after "@policy" - remove "policy" prefix if present
    const policyQuery = query.toLowerCase().startsWith('policy')
      ? query.slice(6).trim()
      : query;
    if (!policyQuery) return policies;
    const lowerQuery = policyQuery.toLowerCase();
    return policies.filter(
      (policy) =>
        policy.name?.toLowerCase().includes(lowerQuery) ||
        policy.settings?.type?.toLowerCase().includes(lowerQuery)
    );
  }, [policies, query, mode]);

  // Filter tasks based on query (only the part after "tasks")
  const filteredTasks = useMemo(() => {
    if (mode !== 'tasks') return [];
    // Extract query after "@tasks" - remove "tasks" prefix if present
    const tasksQuery = query.toLowerCase().startsWith('tasks')
      ? query.slice(5).trim()
      : query;
    if (!tasksQuery) return tasks;
    const lowerQuery = tasksQuery.toLowerCase();
    return tasks.filter(
      (task) =>
        String(task.id).includes(lowerQuery) ||
        task.status?.toLowerCase().includes(lowerQuery) ||
        task.policy_name?.toLowerCase().includes(lowerQuery)
    );
  }, [tasks, query, mode]);

  // Combine and filter documents (folders + files) based on query
  const filteredDocuments = useMemo(() => {
    if (mode !== 'documents') return [];
    // Extract query after "@documents" - remove "documents" prefix if present
    const docsQuery = query.toLowerCase().startsWith('documents')
      ? query.slice(9).trim()
      : query;
    
    // Combine folders and files
    const allItems = [
      ...documentFolders.map((f) => ({ ...f, type: 'folder' })),
      ...documents.map((f) => ({ ...f, type: 'file' })),
    ];
    
    if (!docsQuery) return allItems;
    const lowerQuery = docsQuery.toLowerCase();
    return allItems.filter((item) => item.name?.toLowerCase().includes(lowerQuery));
  }, [documents, documentFolders, query, mode]);

  // Filter functions based on query (only the part after "functions")
  const filteredFunctions = useMemo(() => {
    if (mode !== 'functions') return [];
    // Extract query after "@functions" - remove "functions" prefix if present
    const functionsQuery = query.toLowerCase().startsWith('functions')
      ? query.slice(9).trim()
      : query;
    if (!functionsQuery) return functions;
    const lowerQuery = functionsQuery.toLowerCase();
    return functions.filter(
      (func) =>
        func.name?.toLowerCase().includes(lowerQuery) ||
        func.description?.toLowerCase().includes(lowerQuery) ||
        func.function_type?.toLowerCase().includes(lowerQuery)
    );
  }, [functions, query, mode]);

  // Filter subscribers based on query (only the part after "subscribers")
  const filteredSubscribers = useMemo(() => {
    if (mode !== 'subscribers') return [];
    // Extract query after "@subscribers" - remove "subscribers" prefix if present
    const subscribersQuery = query.toLowerCase().startsWith('subscribers')
      ? query.slice(11).trim()
      : query;
    if (!subscribersQuery) return subscribers;
    const lowerQuery = subscribersQuery.toLowerCase();
    return subscribers.filter(
      (sub) =>
        sub.name?.toLowerCase().includes(lowerQuery) ||
        sub.url?.toLowerCase().includes(lowerQuery)
    );
  }, [subscribers, query, mode]);

  const items = useMemo(() => {
    if (mode === 'types') return filteredTypes;
    if (mode === 'policies') return filteredPolicies;
    if (mode === 'tasks') return filteredTasks;
    if (mode === 'documents') return filteredDocuments;
    if (mode === 'functions') return filteredFunctions;
    if (mode === 'subscribers') return filteredSubscribers;
    return [];
  }, [mode, filteredTypes, filteredPolicies, filteredTasks, filteredDocuments, filteredFunctions, filteredSubscribers]);

  // Handle scroll for infinite loading of tasks
  const handleScroll = useCallback(() => {
    if (mode !== 'tasks' || !hasMoreTasks || tasksLoading) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    const { scrollTop, scrollHeight, clientHeight } = sheet;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onLoadMoreTasks?.();
    }
  }, [mode, hasMoreTasks, tasksLoading, onLoadMoreTasks]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current && highlightedIndex >= 0) {
      const listItems = listRef.current.querySelectorAll('[data-mention-item]');
      if (listItems[highlightedIndex]) {
        listItems[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  if (!open) return null;
  if (!inline && !anchorEl) return null;

  const anchorRect = (!inline && anchorEl) ? anchorEl.getBoundingClientRect() : null;

  const handleSelectType = (type) => {
    onSelectType(type.id);
  };

  const handleSelectPolicy = (policy) => {
    onSelectPolicy(`@policy:${policy.id}`);
  };

  const handleSelectTask = (task) => {
    onSelectTask(`@task:${task.id}`);
  };

  const handleSelectFunction = (func) => {
    onSelectFunction(`@function:${func.id}`);
  };

  const handleSelectSubscriber = (subscriber) => {
    onSelectSubscriber(`@subscriber:${subscriber.id}`);
  };

  const renderContent = () => {
    // Determine loading state based on mode
    let isLoading = loading;
    if (mode === 'tasks') isLoading = tasksLoading && tasks.length === 0;
    if (mode === 'documents') isLoading = documentsLoading;
    if (mode === 'functions') isLoading = functionsLoading;
    if (mode === 'subscribers') isLoading = subscribersLoading;
    
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      );
    }

    // For documents mode, show empty state differently (allow breadcrumbs even when empty)
    if (mode === 'documents' && items.length === 0) {
      return (
        <Box>
          {/* Breadcrumbs for navigation */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Breadcrumbs separator="›" sx={{ fontSize: '0.75rem' }}>
              <Link
                component="button"
                color={documentBreadcrumbs.length === 0 ? 'primary' : 'neutral'}
                onClick={onNavigateDocumentRoot}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
              >
                <HomeIcon sx={{ fontSize: 14 }} />
                Root
              </Link>
              {documentBreadcrumbs.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  color={index === documentBreadcrumbs.length - 1 ? 'primary' : 'neutral'}
                  onClick={() => onNavigateFolder(crumb.path)}
                  underline="hover"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
          <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <FolderIcon sx={{ fontSize: 32, color: 'neutral.400' }} />
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              This folder is empty
            </Typography>
          </Stack>
        </Box>
      );
    }

    if (items.length === 0) {
      let emptyMessage = 'No matching options';
      if (mode === 'policies') {
        emptyMessage = policies.length === 0 ? 'No policies available' : 'No matching policies';
      } else if (mode === 'tasks') {
        emptyMessage = tasks.length === 0 ? 'No tasks available' : 'No matching tasks';
      } else if (mode === 'functions') {
        emptyMessage = functions.length === 0 ? 'No functions available' : 'No matching functions';
      } else if (mode === 'subscribers') {
        emptyMessage = subscribers.length === 0 ? 'No webhooks available' : 'No matching webhooks';
      }
      return (
        <Box sx={{ py: 2, px: 2 }}>
          <Typography level="body-sm" color="neutral">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    if (mode === 'types') {
      return (
        <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
          {filteredTypes.map((type, index) => (
            <ListItem key={type.id} data-mention-item>
              <ListItemButton
                selected={index === highlightedIndex}
                onClick={() => handleSelectType(type)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.softBg' },
                  ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                }}
              >
                <AlternateEmailIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                <ListItemContent>
                  <Typography level="body-sm" fontWeight={500}>
                    @{type.label}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {type.description}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
    }

    // Policies mode
    if (mode === 'policies') {
      return (
        <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
          {filteredPolicies.map((policy, index) => (
            <ListItem key={policy.id} data-mention-item>
              <ListItemButton
                selected={index === highlightedIndex}
                onClick={() => handleSelectPolicy(policy)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.softBg' },
                  ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                }}
              >
                <PolicyIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                <ListItemContent>
                  <Typography level="body-sm" fontWeight={500}>
                    {policy.name}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {policy.settings?.type || 'Policy'}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
    }

    // Tasks mode
    if (mode === 'tasks') {
      return (
        <Box>
          <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
            {filteredTasks.map((task, index) => (
              <ListItem key={task.id} data-mention-item>
                <ListItemButton
                  selected={index === highlightedIndex}
                  onClick={() => handleSelectTask(task)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    gap: 1,
                    '&:hover': { bgcolor: 'primary.softBg' },
                    ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                  }}
                >
                  <TaskIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                  <ListItemContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography level="body-sm" fontWeight={500}>
                        Task #{task.id}
                      </Typography>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={TASK_STATUS_COLORS[task.status] || 'neutral'}
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      >
                        {task.status}
                      </Chip>
                    </Box>
                    <Typography level="body-xs" color="neutral">
                      {task.policy_name || 'No policy'}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {tasksLoading && tasks.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size="sm" />
            </Box>
          )}
          {hasMoreTasks && !tasksLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Button size="sm" variant="plain" onClick={onLoadMoreTasks}>
                Load more
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    // Documents mode
    if (mode === 'documents') {
      return (
        <Box>
          {/* Breadcrumbs for navigation */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Breadcrumbs separator="›" sx={{ fontSize: '0.75rem' }}>
              <Link
                component="button"
                color={documentBreadcrumbs.length === 0 ? 'primary' : 'neutral'}
                onClick={onNavigateDocumentRoot}
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem' }}
              >
                <HomeIcon sx={{ fontSize: 14 }} />
                Root
              </Link>
              {documentBreadcrumbs.map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  color={index === documentBreadcrumbs.length - 1 ? 'primary' : 'neutral'}
                  onClick={() => onNavigateFolder(crumb.path)}
                  underline="hover"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
          {/* File/Folder list */}
          <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
            {filteredDocuments.map((item, index) => {
              const itemName = item.displayName || item.name || '';

              return (
                <ListItem key={getDocumentItemKey(item, index)} data-mention-item>
                  <ListItemButton
                    selected={index === highlightedIndex}
                    onClick={() => {
                      if (item.type === 'folder') {
                        onNavigateFolder(item.path);
                      } else {
                        const filePath = typeof item.path === 'string' ? item.path.trim() : '';
                        if (!filePath) {
                          if (process.env.NODE_ENV !== 'production') {
                            console.warn('Skipping document mention selection due to missing path:', item);
                          }
                          return;
                        }
                        onSelectDocument(item);
                      }
                    }}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      gap: 1,
                      '&:hover': { bgcolor: 'primary.softBg' },
                      ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                    }}
                  >
                    {item.type === 'folder' ? (
                      <FolderIcon sx={{ fontSize: 18, color: '#ffc107' }} />
                    ) : (
                      getFileIcon(itemName)
                    )}
                    <ListItemContent>
                      <Typography level="body-sm" fontWeight={item.type === 'folder' ? 500 : 400}>
                        {itemName}
                      </Typography>
                      {item.type === 'folder' ? (
                        <Typography level="body-xs" color="neutral">
                          Folder
                        </Typography>
                      ) : (
                        <Typography level="body-xs" color="neutral">
                          Click to mention
                        </Typography>
                      )}
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
          {documentsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size="sm" />
            </Box>
          )}
        </Box>
      );
    }

    // Functions mode
    if (mode === 'functions') {
      return (
        <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
          {filteredFunctions.map((func, index) => (
            <ListItem key={func.id} data-mention-item>
              <ListItemButton
                selected={index === highlightedIndex}
                onClick={() => handleSelectFunction(func)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.softBg' },
                  ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                }}
              >
                <FunctionsIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                <ListItemContent>
                  <Typography level="body-sm" fontWeight={500}>
                    {func.name}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {func.function_type || func.description || 'Function'}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
    }

    // Subscribers mode
    if (mode === 'subscribers') {
      return (
        <List ref={listRef} size="sm" sx={{ py: 0.5 }}>
          {filteredSubscribers.map((subscriber, index) => (
            <ListItem key={subscriber.id} data-mention-item>
              <ListItemButton
                selected={index === highlightedIndex}
                onClick={() => handleSelectSubscriber(subscriber)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.softBg' },
                  ...(index === highlightedIndex && { bgcolor: 'primary.softBg' }),
                }}
              >
                <SubscriptionsIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                <ListItemContent>
                  <Typography level="body-sm" fontWeight={500}>
                    {subscriber.name}
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    {subscriber.url || 'Webhook'}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
    }

    return null;
  };

  if (inline) {
    return (
      <Sheet
        ref={sheetRef}
        variant="plain"
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
          borderRadius: 0,
        }}
      >
        {renderContent()}
      </Sheet>
    );
  }

  return (
    <Sheet
      ref={sheetRef}
      variant="outlined"
      onScroll={handleScroll}
      sx={{
        position: 'fixed',
        bottom: `calc(100vh - ${anchorRect.top}px + 4px)`,
        left: anchorRect.left,
        width: Math.min(anchorRect.width, 400),
        maxHeight: 240,
        overflow: 'auto',
        borderRadius: 'sm',
        boxShadow: 'md',
        zIndex: 1300,
      }}
    >
      {renderContent()}
    </Sheet>
  );
};

export default PolicyMentionPopover;
